import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  AlertTitle,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  WarningAmber,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { consumeForcedLogoutReason, type ForcedLogoutReason } from '../utils/sessionEvents';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, loading: authLoading, isAuthenticated } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forcedLogoutReason, setForcedLogoutReason] = useState<ForcedLogoutReason | null>(null);
  const [loading, setLoading] = useState(false);
  const [isTestEnvironment, setIsTestEnvironment] = useState(false);

  // Redirect wenn bereits eingeloggt
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Überprüfe, ob wir in der Test-Umgebung sind
  useEffect(() => {
    setForcedLogoutReason(consumeForcedLogoutReason());
  }, []);

  useEffect(() => {
    const checkEnvironment = async () => {
      try {
        const response = await fetch('/api/config');
        if (response.ok) {
          const config = await response.json();
          if (config.backendUrl === 'https://materialmanager-production.up.railway.app') {
            setIsTestEnvironment(true);
          }
        }
      } catch (err) {
        // Fehler stillschweigend behandeln
        console.warn('Could not fetch config:', err);
      }
    };
    checkEnvironment();
  }, []);

  // Zeige Loading-Spinner während Auth-Status geprüft wird
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setForcedLogoutReason(null);

    if (!username || !password) {
      setError('Bitte füllen Sie alle Felder aus');
      return;
    }

    setLoading(true);

    try {
      await login(username, password);
      navigate('/'); // Zur Startseite nach erfolgreichem Login
    } catch (err: any) {
      setError(err.message || 'Login fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <LoginIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h4" gutterBottom>
            Material Manager
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Melden Sie sich an, um fortzufahren
          </Typography>
        </Box>

        {isTestEnvironment && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            icon={<WarningAmber />}
          >
            <AlertTitle>Achtung: Testumgebung</AlertTitle>
            Sie sind bei der <strong>Testumgebung</strong> angemeldet, nicht bei der Produktivumgebung. 
            Daten können hier ohne Warnung gelöscht werden. Behandeln Sie diese Umgebung nur für Test- und Entwicklungszwecke.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {forcedLogoutReason && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {forcedLogoutReason.title && <AlertTitle>{forcedLogoutReason.title}</AlertTitle>}
            {forcedLogoutReason.message}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Benutzername"
            variant="outlined"
            margin="normal"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Passwort"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
          >
            {loading ? 'Anmelden...' : 'Anmelden'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default Login;
